import React from "react";
import { FormHelperText, Typography } from "@mui/material";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { type UploadEntry } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import { TextField } from "@components/Input/Input.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import {
  isTerminalServiceUploadedEntry,
  useTerminalServiceDocumentUpload,
} from "@features/terminal-services/hooks/useTerminalServiceDocumentUpload";
import type { TerminalServiceDocument } from "@services/terminal-services/types";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

const LANG_BASE = "terminal-services.cancel";
const TEST_ID_BASE = "terminal-service-cancel-drawer";
const REASON_MAX_CHARS = 500;
const ALLOWED_ATTACHMENT_FORMATS = ["pdf", "jpg", "jpeg", "png", "xls", "docx"];
const ATTACHMENT_VALIDATION_MESSAGE =
  "File must be under 10MB and in one of the following formats: PDF, JPG, JPEG, PNG, XLS, DOCX.";
const CANCELLATION_ATTACHMENT_DOCUMENT_TYPE = "DOCUMENT";

type CancelTerminalServiceFormData = Record<string, File[]>;

export type TerminalServiceCancelDraft = {
  cancellationReason: string;
};

type CancelTerminalServiceDrawerProps = {
  isOpen: boolean;
  requestId?: string | null;
  draft: TerminalServiceCancelDraft;
  onDraftChange: (draft: TerminalServiceCancelDraft) => void;
  onClose: (options?: { resetDraft?: boolean }) => void;
  onCancelService: (payload: {
    cancellationReason: string;
    attachment: TerminalServiceDocument | null;
  }) => void;
  isSubmitting?: boolean;
};

const toAllowedExtensions = (extensions: string[]) =>
  extensions.map((extension) =>
    extension.startsWith(".") ? extension.toLowerCase() : `.${extension.toLowerCase()}`,
  );

const getUploadedAttachment = (entry?: UploadEntry): TerminalServiceDocument | null => {
  if (!entry || !isTerminalServiceUploadedEntry(entry)) {
    return null;
  }

  return {
    documentUrl: entry.url,
    documentName: entry.name,
    documentType: CANCELLATION_ATTACHMENT_DOCUMENT_TYPE,
  };
};

const CancelTerminalServiceDrawer = ({
  isOpen,
  requestId,
  draft,
  onDraftChange,
  onClose,
  onCancelService,
  isSubmitting = false,
}: CancelTerminalServiceDrawerProps) => {
  const { t } = useTranslation();
  const fileProfiles = useFileUploadProfiles();
  const uploadProfile = fileProfiles.additionalDocuments;
  const uploadConditions = {
    ...uploadProfile,
    format: ALLOWED_ATTACHMENT_FORMATS,
    hideErrors: true,
  };
  const storeKey = React.useMemo(
    () => `terminalServiceCancelAttachment-${requestId ?? "unknown"}`,
    [requestId],
  );
  const attachmentForm = useForm<CancelTerminalServiceFormData>({
    defaultValues: { [storeKey]: [] },
  });
  const [reasonError, setReasonError] = React.useState<string>();
  const { uploadEntries, setUploadEntries, handleUpload, resetUpload, isUploading } =
    useTerminalServiceDocumentUpload({
      storeKey,
      certificateType: "FOTT_SERVICE_CANCELLATION_ATTACHMENT",
      fallbackFileNamePrefix: "cancellation-attachment",
      errorNotification: {
        titleKey: `${LANG_BASE}.notifications.upload-error-title`,
        descriptionKey: `${LANG_BASE}.notifications.upload-error-description`,
      },
    });

  const resetDrawerData = React.useCallback(() => {
    onDraftChange({ cancellationReason: "" });
    setReasonError(undefined);
    attachmentForm.reset({ [storeKey]: [] });
    resetUpload();
  }, [attachmentForm, onDraftChange, resetUpload, storeKey]);

  const handleCloseWithReset = React.useCallback(() => {
    resetDrawerData();
    onClose({ resetDraft: true });
  }, [onClose, resetDrawerData]);

  const validateReason = React.useCallback(
    (value: string) => {
      const trimmedValue = value.trim();

      if (!trimmedValue) {
        return t("form-validation.required", {
          label: t(`${LANG_BASE}.fields.cancellation-reason`, {
            defaultValue: "Cancellation Reason",
          }),
        });
      }

      if (trimmedValue.length > REASON_MAX_CHARS) {
        return t("form-validation.max-length", {
          label: t(`${LANG_BASE}.fields.cancellation-reason`, {
            defaultValue: "Cancellation Reason",
          }),
          max: REASON_MAX_CHARS,
        });
      }

      return undefined;
    },
    [t],
  );

  const handleSubmit = React.useCallback(() => {
    const nextReasonError = validateReason(draft.cancellationReason);
    setReasonError(nextReasonError);

    if (nextReasonError) {
      return;
    }

    onCancelService({
      cancellationReason: draft.cancellationReason.trim(),
      attachment: getUploadedAttachment(uploadEntries[0]),
    });
  }, [draft.cancellationReason, onCancelService, uploadEntries, validateReason]);

  const allowedExtensions = React.useMemo(
    () => toAllowedExtensions(ALLOWED_ATTACHMENT_FORMATS),
    [],
  );

  const removeAttachment = React.useCallback(() => {
    attachmentForm.setValue(storeKey, []);
    attachmentForm.clearErrors(storeKey);
    resetUpload();
  }, [attachmentForm, resetUpload, storeKey]);

  const isPrintableKey = (event: React.KeyboardEvent) =>
    event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;

  if (!isOpen) {
    return null;
  }

  return (
    <div data-testid={TEST_ID_BASE} className="w-[400px] max-w-[100vw]">
      <SideDrawer
        isOpen={isOpen}
        title={t(`${LANG_BASE}.title`, { defaultValue: "Cancel FOTT Service" })}
        onClose={() => onClose({ resetDraft: false })}
        onCancel={handleCloseWithReset}
        submitLabel={t(`${LANG_BASE}.submit`, { defaultValue: "Confirm Cancellation" })}
        cancelLabel={t("common.cancel")}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        isSubmitDisabled={isUploading}
      >
        <div className="flex-1 w-[400px] max-w-[100vw] py-2">
          <div className="flex flex-col gap-4 px-6">
            <div className="flex flex-col gap-2">
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t(`${LANG_BASE}.fields.cancellation-reason`, {
                  defaultValue: "Cancellation Reason",
                })}
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={4}
                value={draft.cancellationReason}
                error={Boolean(reasonError)}
                inputProps={{ maxLength: REASON_MAX_CHARS }}
                onKeyDown={(event) => {
                  if (
                    isPrintableKey(event) &&
                    draft.cancellationReason.length >= REASON_MAX_CHARS
                  ) {
                    setReasonError(
                      t("form-validation.max-length", {
                        label: t(`${LANG_BASE}.fields.cancellation-reason`, {
                          defaultValue: "Cancellation Reason",
                        }),
                        max: REASON_MAX_CHARS,
                      }),
                    );
                  }
                }}
                onChange={(event) => {
                  const value = event.target.value;
                  onDraftChange({ cancellationReason: value });
                  setReasonError(validateReason(value));
                }}
                data-testid={`${TEST_ID_BASE}-reason`}
              />
              {reasonError && (
                <FormHelperText
                  data-testid={`${TEST_ID_BASE}-reason-error`}
                  sx={{ color: "error.main", m: 0 }}
                >
                  {reasonError}
                </FormHelperText>
              )}
              <FormHelperText sx={{ ml: 0, mt: 0.5, fontSize: 12, textAlign: "right" }}>
                {draft.cancellationReason.length}/{REASON_MAX_CHARS}
              </FormHelperText>
            </div>

            <FormProvider {...attachmentForm}>
              <FormItem<CancelTerminalServiceFormData>
                name={storeKey}
                type="file"
                maxFiles={1}
                label={t(`${LANG_BASE}.fields.attachment`, { defaultValue: "Attachment" })}
                testId={`${TEST_ID_BASE}-attachment-upload`}
                uploadConditions={uploadConditions}
                allowedFileExtensions={allowedExtensions}
                uploadDescription={ATTACHMENT_VALIDATION_MESSAGE}
                files={uploadEntries}
                setFiles={setUploadEntries}
                onUpload={(_, files) => {
                  attachmentForm.clearErrors(storeKey);
                  handleUpload(files);
                }}
                onReject={() =>
                  attachmentForm.setError(storeKey, {
                    type: "manual",
                    message: ATTACHMENT_VALIDATION_MESSAGE,
                  })
                }
                onRemoveFile={removeAttachment}
                disabled={isSubmitting || isUploading}
              />
              {uploadEntries[0] && (
                <SingleFile
                  testId={`${TEST_ID_BASE}-attachment`}
                  file={uploadEntries[0]}
                  fieldName={storeKey}
                  currentFileIndex={0}
                  setFiles={removeAttachment}
                  removeFn={removeAttachment}
                  isLoading={isUploading && uploadEntries[0] instanceof File}
                />
              )}
            </FormProvider>
          </div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default CancelTerminalServiceDrawer;
